import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuietMode } from './QuietModeProvider'
import { useTheme } from './ThemeProvider'
import { 
  getUserRoutines, 
  saveUserRoutines,
  recordTaskCompletion, 
  recordTaskSkip,
  getRoutineInsights,
  getStreakForBlock,
  checkForReminders,
  markReminderShown,
  markReminderSkipped,
  resetReminderSkipCount,
  updateReminderSettings,
  type RoutineBlock, 
  type RoutineTask,
  type RoutineStep
} from '../utils/routineStorage'
import DopaRoutineSupport from './DopaRoutineSupport'
import RoutineCelebration from './RoutineCelebration'
import CustomRoutineCreator from './CustomRoutineCreator'
import GentleReminder from './GentleReminder'
import ReminderSettings from './ReminderSettings'

interface ActiveSession {
  blockId: string
  taskId: string
  stepIndex: number
  startTime: Date
  moodBefore?: 'low' | 'okay' | 'good'
  skippedSteps: string[]
}

const RoutineBuilder = () => {
  const navigate = useNavigate()
  const { isQuietMode, isLowStimMode } = useQuietMode()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [routines, setRoutines] = useState<RoutineBlock[]>([])
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null)
  const [isLowEnergyMode, setIsLowEnergyMode] = useState(false)
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set())
  const [showCelebration, setShowCelebration] = useState<{
    type: 'task' | 'block' | 'streak'
    message: string
    blockName?: string
    streakCount?: number
  } | null>(null)
  const [showDopaSupport, setShowDopaSupport] = useState<{
    type: 'hesitation' | 'skip_pattern' | 'encouragement'
    context: string
  } | null>(null)
  const [showCustomCreator, setShowCustomCreator] = useState(false)
  const [editingRoutine, setEditingRoutine] = useState<RoutineBlock | null>(null)
  const [activeReminder, setActiveReminder] = useState<any>(null)
  const [showReminderSettings, setShowReminderSettings] = useState<RoutineBlock | null>(null)

  useEffect(() => {
    const loadedRoutines = getUserRoutines()
    setRoutines(loadedRoutines)
    
    // Auto-expand morning routines if it's morning
    const hour = new Date().getHours()
    if (hour >= 6 && hour < 12) {
      const morningBlocks = loadedRoutines.filter(r => r.time_range === 'morning')
      setExpandedBlocks(new Set(morningBlocks.map(b => b.id)))
    }

    // Check for reminders
    const checkReminders = () => {
      const pendingReminders = checkForReminders()
      if (pendingReminders.length > 0 && !activeReminder) {
        const reminder = pendingReminders[0]
        const routine = loadedRoutines.find(r => r.id === reminder.block_id)
        if (routine) {
          setActiveReminder({ reminder, routine })
        }
      }
    }

    // Check immediately and then every minute
    checkReminders()
    const reminderInterval = setInterval(checkReminders, 60000)

    return () => clearInterval(reminderInterval)
  }, [activeReminder])

  const insights = getRoutineInsights()

  const getFilteredTasks = (tasks: RoutineTask[]) => {
    if (isLowEnergyMode) {
      return tasks.filter(task => task.is_essential)
    }
    return tasks
  }

  const getFilteredSteps = (steps: RoutineStep[]) => {
    if (isLowEnergyMode) {
      return steps.filter(step => !step.is_optional)
    }
    return steps
  }

  const toggleBlockExpansion = (blockId: string) => {
    const newExpanded = new Set(expandedBlocks)
    if (newExpanded.has(blockId)) {
      newExpanded.delete(blockId)
    } else {
      newExpanded.add(blockId)
    }
    setExpandedBlocks(newExpanded)
  }

  const startTask = (block: RoutineBlock, task: RoutineTask, moodBefore?: 'low' | 'okay' | 'good') => {
    setActiveSession({
      blockId: block.id,
      taskId: task.id,
      stepIndex: 0,
      startTime: new Date(),
      moodBefore,
      skippedSteps: []
    })

    // Show Dopa encouragement
    setShowDopaSupport({
      type: 'encouragement',
      context: `Starting "${task.task_name}" in ${block.name}`
    })
  }

  const completeStep = () => {
    if (!activeSession) return

    const block = routines.find(r => r.id === activeSession.blockId)
    const task = block?.tasks.find(t => t.id === activeSession.taskId)
    
    if (!task) return

    const filteredSteps = getFilteredSteps(task.steps)
    const nextStepIndex = activeSession.stepIndex + 1
    
    if (nextStepIndex >= filteredSteps.length) {
      // Task completed
      completeTask()
    } else {
      // Move to next step
      setActiveSession({
        ...activeSession,
        stepIndex: nextStepIndex
      })
    }
  }

  const skipStep = () => {
    if (!activeSession) return

    const block = routines.find(r => r.id === activeSession.blockId)
    const task = block?.tasks.find(t => t.id === activeSession.taskId)
    
    if (!task) return

    const filteredSteps = getFilteredSteps(task.steps)
    const currentStep = filteredSteps[activeSession.stepIndex]
    
    if (currentStep && currentStep.is_optional) {
      // Add to skipped steps
      const newSkippedSteps = [...activeSession.skippedSteps, currentStep.id]
      
      const nextStepIndex = activeSession.stepIndex + 1
      
      if (nextStepIndex >= filteredSteps.length) {
        // Task completed with skipped steps
        setActiveSession({ ...activeSession, skippedSteps: newSkippedSteps })
        completeTask()
      } else {
        // Move to next step
        setActiveSession({
          ...activeSession,
          stepIndex: nextStepIndex,
          skippedSteps: newSkippedSteps
        })
      }
    }
  }

  const completeTask = (moodAfter?: 'low' | 'okay' | 'good') => {
    if (!activeSession) return

    const block = routines.find(r => r.id === activeSession.blockId)
    const task = block?.tasks.find(t => t.id === activeSession.taskId)
    
    if (!block || !task) return

    const completionTime = Math.floor((Date.now() - activeSession.startTime.getTime()) / 1000)
    
    recordTaskCompletion(
      activeSession.taskId,
      activeSession.blockId,
      completionTime,
      activeSession.moodBefore,
      moodAfter,
      undefined,
      activeSession.skippedSteps
    )

    // Check if block is completed
    const blockTasks = getFilteredTasks(block.tasks)
    const completedToday = blockTasks.filter(t => {
      // This would check today's progress - simplified for demo
      return t.id === activeSession.taskId // Just completed this task
    })

    if (completedToday.length === blockTasks.length) {
      // Block completed!
      const streak = getStreakForBlock(block.id)
      setShowCelebration({
        type: 'block',
        message: `Beautiful! You completed your entire ${block.name} routine.`,
        blockName: block.name,
        streakCount: streak?.current_streak
      })
    } else {
      // Task completed
      const skippedCount = activeSession.skippedSteps.length
      const message = skippedCount > 0 
        ? `You did great. "${task.task_name}" is complete. (${skippedCount} optional step${skippedCount !== 1 ? 's' : ''} skipped)`
        : `You did great. "${task.task_name}" is complete.`
      
      setShowCelebration({
        type: 'task',
        message
      })
    }

    setActiveSession(null)
    setShowDopaSupport(null)
  }

  const skipTask = (reason?: string) => {
    if (!activeSession) return

    recordTaskSkip(activeSession.taskId, activeSession.blockId, reason)
    
    // Show gentle Dopa response
    setShowDopaSupport({
      type: 'skip_pattern',
      context: `Skipped task in routine`
    })

    setActiveSession(null)
  }

  const pauseSession = () => {
    setActiveSession(null)
    setShowDopaSupport({
      type: 'hesitation',
      context: 'User paused mid-task'
    })
  }

  const getCurrentStep = (): RoutineStep | null => {
    if (!activeSession) return null
    
    const block = routines.find(r => r.id === activeSession.blockId)
    const task = block?.tasks.find(t => t.id === activeSession.taskId)
    
    if (!task) return null
    
    const filteredSteps = getFilteredSteps(task.steps)
    return filteredSteps[activeSession.stepIndex] || null
  }

  const getStepProgress = (): number => {
    if (!activeSession) return 0
    
    const block = routines.find(r => r.id === activeSession.blockId)
    const task = block?.tasks.find(t => t.id === activeSession.taskId)
    
    if (!task) return 0
    
    const filteredSteps = getFilteredSteps(task.steps)
    return ((activeSession.stepIndex + 1) / filteredSteps.length) * 100
  }

  const handleSaveCustomRoutine = (routine: RoutineBlock) => {
    const updatedRoutines = editingRoutine 
      ? routines.map(r => r.id === editingRoutine.id ? routine : r)
      : [...routines, routine]
    
    setRoutines(updatedRoutines)
    saveUserRoutines(updatedRoutines)
    setShowCustomCreator(false)
    setEditingRoutine(null)
    
    // Show celebration for creating new routine
    if (!editingRoutine) {
      setShowCelebration({
        type: 'block',
        message: `Your custom routine "${routine.name}" has been created! Ready to start building this new habit?`,
        blockName: routine.name
      })
    }
  }

  const handleEditRoutine = (routine: RoutineBlock) => {
    setEditingRoutine(routine)
    setShowCustomCreator(true)
  }

  const handleDeleteRoutine = (routineId: string) => {
    const updatedRoutines = routines.filter(r => r.id !== routineId)
    setRoutines(updatedRoutines)
    saveUserRoutines(updatedRoutines)
  }

  // Reminder handlers
  const handleReminderStart = () => {
    if (activeReminder) {
      markReminderShown(activeReminder.reminder.id)
      resetReminderSkipCount(activeReminder.reminder.id)
      
      // Start the routine
      const routine = activeReminder.routine
      if (routine.tasks.length > 0) {
        startTask(routine, routine.tasks[0])
      }
      
      setActiveReminder(null)
    }
  }

  const handleReminderNotNow = () => {
    if (activeReminder) {
      markReminderShown(activeReminder.reminder.id)
      setActiveReminder(null)
    }
  }

  const handleReminderSkipToday = () => {
    if (activeReminder) {
      markReminderSkipped(activeReminder.reminder.id)
      setActiveReminder(null)
    }
  }

  const handleReminderPause = () => {
    if (activeReminder) {
      updateReminderSettings(activeReminder.routine.id, false)
      setActiveReminder(null)
    }
  }

  // Active session view
  if (activeSession) {
    const currentStep = getCurrentStep()
    const block = routines.find(r => r.id === activeSession.blockId)
    const task = block?.tasks.find(t => t.id === activeSession.taskId)
    const progress = getStepProgress()

    if (!currentStep || !task || !block) {
      setActiveSession(null)
      return null
    }

    const filteredSteps = getFilteredSteps(task.steps)
    const isOptionalStep = currentStep.is_optional

    return (
      <div className="min-h-screen bg-sage-25 dark:bg-sage-900 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-sage-50/60 dark:bg-sage-800/60 border-b border-sage-100/30 dark:border-sage-700/30">
          <button
            onClick={pauseSession}
            className="p-3 sm:p-4 rounded-2xl hover:bg-sage-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
          >
            <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
            </svg>
          </button>
          <div className="text-center">
            <h1 className="text-base sm:text-lg font-light text-sage-600 dark:text-sage-300">{task.task_name}</h1>
            <p className="text-sage-500 dark:text-sage-400 text-xs sm:text-sm font-light">{block.name}</p>
          </div>
          <div className="w-11 sm:w-12"></div>
        </div>

        {/* Progress */}
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 bg-sage-50/40 dark:bg-sage-800/40">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sage-500 dark:text-sage-400 text-xs sm:text-sm font-light">
                Step {activeSession.stepIndex + 1} of {filteredSteps.length}
              </span>
              <span className="text-sage-500 dark:text-sage-400 text-xs sm:text-sm font-light">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full bg-sage-100/50 dark:bg-sage-700/50 rounded-full h-2">
              <div 
                className="bg-sage-300 dark:bg-sage-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            {activeSession.skippedSteps.length > 0 && (
              <p className="text-sage-400 dark:text-sage-500 text-xs font-light mt-1 text-center">
                {activeSession.skippedSteps.length} optional step{activeSession.skippedSteps.length !== 1 ? 's' : ''} skipped
              </p>
            )}
          </div>
        </div>

        {/* Current Step */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-center mb-8 sm:mb-12">
            <div className="w-16 h-16 mx-auto mb-4 sm:mb-6 bg-sage-100/60 dark:bg-sage-700/60 rounded-full flex items-center justify-center border border-sage-200/30 dark:border-sage-600/30">
              <span className="text-2xl">🌱</span>
            </div>
            
            <h2 className="text-sage-600 dark:text-sage-300 font-light text-lg sm:text-xl mb-4 sm:mb-6 leading-relaxed max-w-md mx-auto">
              {currentStep.step_text}
              {isOptionalStep && (
                <span className="block text-sage-400 dark:text-sage-500 text-xs sm:text-sm font-light mt-2">
                  (This step is optional)
                </span>
              )}
            </h2>
            
            {task.description && (
              <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed max-w-sm mx-auto mb-6 sm:mb-8">
                {task.description}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-4 w-full max-w-sm">
            <button
              onClick={completeStep}
              className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-sage-100/60 hover:bg-sage-200/50 
                         dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                         text-sage-600 dark:text-sage-300 rounded-2xl text-sm sm:text-base font-light 
                         border border-sage-200/40 dark:border-sage-600/40 
                         hover:border-sage-300/50 dark:hover:border-sage-500/50
                         transition-colors duration-500 focus:outline-none focus:ring-4 
                         focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
            >
              {activeSession.stepIndex + 1 === filteredSteps.length ? 'Complete Task' : 'Next Step'}
            </button>
            
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              {isOptionalStep && (
                <button
                  onClick={skipStep}
                  className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-mint-50/60 hover:bg-mint-100/50 
                             dark:bg-mint-900/30 dark:hover:bg-mint-800/40
                             text-mint-600 dark:text-mint-300 rounded-2xl text-xs sm:text-sm font-light 
                             border border-mint-100/40 dark:border-mint-700/30 
                             hover:border-mint-200/50 dark:hover:border-mint-600/40
                             transition-colors duration-300 focus:outline-none focus:ring-4 
                             focus:ring-mint-100/50 dark:focus:ring-mint-700/30"
                >
                  Skip (Optional)
                </button>
              )}
              
              <button
                onClick={pauseSession}
                className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-cream-50/60 hover:bg-cream-100/50 
                           dark:bg-cream-900/20 dark:hover:bg-cream-800/30
                           text-cream-600 dark:text-cream-300 rounded-2xl text-xs sm:text-sm font-light 
                           border border-cream-100/40 dark:border-cream-700/30 
                           hover:border-cream-200/50 dark:hover:border-cream-600/40
                           transition-colors duration-300 focus:outline-none focus:ring-4 
                           focus:ring-cream-100/50 dark:focus:ring-cream-700/30"
                >
                  Pause
                </button>
              
              <button
                onClick={() => skipTask('Too difficult right now')}
                className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-neutral-50/60 hover:bg-neutral-100/50 
                           dark:bg-neutral-800/40 dark:hover:bg-neutral-700/50
                           text-neutral-500 dark:text-neutral-400 rounded-2xl text-xs sm:text-sm font-light 
                           border border-neutral-100/40 dark:border-neutral-700/30 
                           hover:border-neutral-200/50 dark:hover:border-neutral-600/40
                           transition-colors duration-300 focus:outline-none focus:ring-4 
                           focus:ring-neutral-100/50 dark:focus:ring-neutral-700/30"
                >
                  Skip Task
                </button>
            </div>
          </div>
        </div>

        {/* Return to Tools Button */}
        <div className="text-center mb-6">
          <button
            onClick={() => navigate('/tools')}
            className="text-sage-400 dark:text-sage-500 text-xs sm:text-sm font-light hover:text-sage-500 dark:hover:text-sage-400 transition-colors"
          >
            Return to tools
          </button>
        </div>

        {/* Dopa Support */}
        {showDopaSupport && (
          <DopaRoutineSupport
            type={showDopaSupport.type}
            context={showDopaSupport.context}
            onClose={() => setShowDopaSupport(null)}
          />
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-sage-25 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-sage-50/60 dark:bg-sage-800/60 border-b border-sage-100/30 dark:border-sage-700/30">
        <button
          onClick={() => navigate('/tools')}
          className="p-3 sm:p-4 rounded-2xl hover:bg-sage-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
        >
          <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="text-center">
          <h1 className="text-base sm:text-lg font-light text-sage-600 dark:text-sage-300">Routine Builder</h1>
          {!isQuietMode && (
            <p className="text-sage-500 dark:text-sage-400 text-xs sm:text-sm font-light">
              {insights.todayCompletions} completed today
            </p>
          )}
        </div>
        <button
          onClick={() => setShowCustomCreator(true)}
          className="p-3 sm:p-4 rounded-2xl hover:bg-mint-100/60 dark:hover:bg-mint-800/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-mint-100/50 dark:focus:ring-mint-700/50"
        >
          <svg className="w-5 h-5 text-mint-500 dark:text-mint-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Mode Toggle */}
      <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 bg-sage-50/40 dark:bg-sage-800/40 border-b border-sage-100/20 dark:border-sage-700/20">
        <div className="flex items-center justify-center space-x-4">
          <span className={`text-xs sm:text-sm font-light ${isLowEnergyMode ? 'text-sage-400 dark:text-sage-500' : 'text-sage-600 dark:text-sage-300'}`}>
            Full Routine
          </span>
          <button
            onClick={() => setIsLowEnergyMode(!isLowEnergyMode)}
            className={`relative inline-flex h-5 sm:h-6 w-10 sm:w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-sage-200 dark:focus:ring-sage-600 focus:ring-offset-2 ${
              isLowEnergyMode ? 'bg-mint-300 dark:bg-mint-600' : 'bg-sage-200 dark:bg-sage-600'
            }`}
          >
            <span
              className={`inline-block h-3 sm:h-4 w-3 sm:w-4 transform rounded-full bg-white transition-transform duration-300 ${
                isLowEnergyMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-xs sm:text-sm font-light ${isLowEnergyMode ? 'text-mint-600 dark:text-mint-300' : 'text-sage-400 dark:text-sage-500'}`}>
            Low-Energy Mode
          </span>
        </div>
        {isLowEnergyMode && !isQuietMode && (
          <p className="text-center text-mint-500 dark:text-mint-400 text-xs font-light mt-2">
            Showing only essential tasks and required steps
          </p>
        )}
      </div>

      {/* Create New Routine Button */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <button
          onClick={() => setShowCustomCreator(true)}
          className="w-full p-4 sm:p-6 bg-mint-50/40 hover:bg-mint-100/50 
                     dark:bg-mint-900/30 dark:hover:bg-mint-800/40
                     text-mint-600 dark:text-mint-300 rounded-3xl 
                     border border-mint-100/40 dark:border-mint-700/30 
                     hover:border-mint-200/50 dark:hover:border-mint-600/40
                     transition-colors duration-500 focus:outline-none focus:ring-4 
                     focus:ring-mint-100/50 dark:focus:ring-mint-700/30
                     border-dashed"
        >
          <div className="flex items-center justify-center space-x-2 sm:space-x-3">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="font-light text-sm sm:text-base">Create Custom Routine</span>
          </div>
          {!isQuietMode && (
            <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm mt-2">
              Build a routine that fits your unique needs
            </p>
          )}
        </button>
      </div>

      {/* Routines */}
      <div className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 space-y-4 sm:space-y-6">
        {routines.map((block) => {
          const filteredTasks = getFilteredTasks(block.tasks)
          const isExpanded = expandedBlocks.has(block.id)
          const streak = getStreakForBlock(block.id)
          const isCustom = !['morning-reset', 'evening-wind-down'].includes(block.id)

          return (
            <div key={block.id} className="bg-white/60 dark:bg-sage-800/60 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 overflow-hidden">
              {/* Block Header */}
              <button
                onClick={() => toggleBlockExpansion(block.id)}
                className="w-full p-4 sm:p-6 text-left hover:bg-sage-50/40 dark:hover:bg-sage-700/40 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-2">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg">{block.name}</h3>
                      {isCustom && (
                        <span className="px-2 py-1 bg-mint-100/60 dark:bg-mint-900/60 text-mint-600 dark:text-mint-300 text-xs font-light rounded-full">
                          Custom
                        </span>
                      )}
                      {streak && streak.current_streak > 0 && (
                        <div className="flex items-center space-x-1">
                          <span className="text-orange-400 dark:text-orange-300 text-sm">🔥</span>
                          <span className="text-sage-500 dark:text-sage-400 text-xs sm:text-sm font-light">{streak.current_streak}</span>
                        </div>
                      )}
                    </div>
                    {!isQuietMode && block.description && (
                      <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed">
                        {block.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                      <span className="text-sage-400 dark:text-sage-500 text-xs font-light capitalize">
                        {block.time_range}
                      </span>
                      <span className="text-sage-400 dark:text-sage-500 text-xs font-light">
                        {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sage-400 dark:text-sage-500 text-xs font-light">
                        ~{filteredTasks.reduce((sum, task) => sum + task.estimated_minutes, 0)} min
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Reminder settings button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowReminderSettings(block)
                      }}
                      className="p-2 rounded-xl hover:bg-cream-100/60 dark:hover:bg-cream-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-cream-100 dark:focus:ring-cream-700"
                    >
                      <svg className="w-4 h-4 text-cream-500 dark:text-cream-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    
                    {isCustom && (
                      <div className="flex space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditRoutine(block)
                          }}
                          className="p-2 rounded-xl hover:bg-cream-100/60 dark:hover:bg-cream-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-cream-100 dark:focus:ring-cream-700"
                        >
                          <svg className="w-4 h-4 text-cream-500 dark:text-cream-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (confirm(`Delete "${block.name}" routine? This cannot be undone.`)) {
                              handleDeleteRoutine(block.id)
                            }
                          }}
                          className="p-2 rounded-xl hover:bg-peach-100/60 dark:hover:bg-peach-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-peach-100 dark:focus:ring-peach-700"
                        >
                          <svg className="w-4 h-4 text-peach-500 dark:text-peach-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <svg 
                      className={`w-5 h-5 text-sage-400 dark:text-sage-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </button>

              {/* Tasks */}
              {isExpanded && (
                <div className="border-t border-sage-100/30 dark:border-sage-700/30 bg-sage-50/20 dark:bg-sage-800/20">
                  {filteredTasks.map((task, index) => {
                    const filteredSteps = getFilteredSteps(task.steps)
                    const optionalStepsCount = task.steps.filter(s => s.is_optional).length
                    const requiredStepsCount = task.steps.filter(s => !s.is_optional).length
                    
                    return (
                      <div key={task.id} className={`p-4 sm:p-6 ${index > 0 ? 'border-t border-sage-100/20 dark:border-sage-700/20' : ''}`}>
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="text-sage-600 dark:text-sage-300 font-light text-sm sm:text-base mb-2">{task.task_name}</h4>
                            {!isQuietMode && task.description && (
                              <p className="text-sage-500 dark:text-sage-400 font-light text-xs sm:text-sm leading-relaxed mb-3">
                                {task.description}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 sm:mb-4">
                              <span className="text-sage-400 dark:text-sage-500 text-xs font-light">
                                {task.estimated_minutes} min
                              </span>
                              <span className="text-sage-400 dark:text-sage-500 text-xs font-light">
                                {filteredSteps.length} step{filteredSteps.length !== 1 ? 's' : ''}
                                {isLowEnergyMode && optionalStepsCount > 0 && (
                                  <span className="text-sage-500 dark:text-sage-400"> ({optionalStepsCount} optional hidden)</span>
                                )}
                              </span>
                              {task.is_essential && (
                                <span className="text-mint-500 dark:text-mint-400 text-xs font-light">Essential</span>
                              )}
                            </div>
                            
                            {/* Steps Preview */}
                            {!isQuietMode && (
                              <div className="space-y-1 mb-4 hidden sm:block">
                                {filteredSteps.slice(0, 3).map((step, stepIndex) => (
                                  <div key={step.id} className="flex items-center space-x-2">
                                    <div className={`w-1 h-1 rounded-full ${step.is_optional ? 'bg-sage-300 dark:bg-sage-600' : 'bg-sage-400 dark:bg-sage-500'}`}></div>
                                    <span className={`text-xs font-light ${step.is_optional ? 'text-sage-400 dark:text-sage-500 italic' : 'text-sage-500 dark:text-sage-400'}`}>
                                      {step.step_text}
                                      {step.is_optional && !isLowEnergyMode && <span className="ml-1">(optional)</span>}
                                    </span>
                                  </div>
                                ))}
                                {filteredSteps.length > 3 && (
                                  <div className="flex items-center space-x-2">
                                    <div className="w-1 h-1 bg-sage-300 dark:bg-sage-600 rounded-full"></div>
                                    <span className="text-sage-400 dark:text-sage-500 text-xs font-light">
                                      +{filteredSteps.length - 3} more steps
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <button
                            onClick={() => startTask(block, task)}
                            className="ml-4 px-4 sm:px-6 py-2 sm:py-3 bg-sage-100/60 hover:bg-sage-200/50 
                                       dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                                       text-sage-600 dark:text-sage-300 rounded-2xl text-xs sm:text-sm font-light 
                                       border border-sage-200/40 dark:border-sage-600/40 
                                       hover:border-sage-300/50 dark:hover:border-sage-500/50
                                       transition-colors duration-300 focus:outline-none focus:ring-4 
                                       focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
                          >
                            Start
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Insights */}
      {!isQuietMode && (
        <div className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
          <div className="bg-cream-50/40 dark:bg-sage-800/40 rounded-3xl border border-cream-100/30 dark:border-sage-700/30 p-4 sm:p-6">
            <h3 className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-4">Your Progress</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-cream-600 dark:text-cream-300 font-light text-xl sm:text-2xl">{insights.todayCompletions}</div>
                <div className="text-cream-500 dark:text-cream-400 font-light text-xs">Today</div>
              </div>
              <div className="text-center">
                <div className="text-cream-600 dark:text-cream-300 font-light text-xl sm:text-2xl">{insights.activeStreaks}</div>
                <div className="text-cream-500 dark:text-cream-400 font-light text-xs">Active Streaks</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Return to Tools Button */}
      <div className="text-center pb-6">
        <button
          onClick={() => navigate('/tools')}
          className="text-sage-400 dark:text-sage-500 text-xs sm:text-sm font-light hover:text-sage-500 dark:hover:text-sage-400 transition-colors"
        >
          Return to tools
        </button>
      </div>

      {/* Gentle Reminder */}
      {activeReminder && (
        <GentleReminder
          reminder={activeReminder.reminder}
          routine={activeReminder.routine}
          onStart={handleReminderStart}
          onNotNow={handleReminderNotNow}
          onSkipToday={handleReminderSkipToday}
          onPauseReminders={handleReminderPause}
        />
      )}

      {/* Reminder Settings */}
      {showReminderSettings && (
        <ReminderSettings
          routine={showReminderSettings}
          onClose={() => setShowReminderSettings(null)}
          onSave={() => {
            setShowReminderSettings(null)
            // Reload routines to get updated reminder settings
            const updatedRoutines = getUserRoutines()
            setRoutines(updatedRoutines)
          }}
        />
      )}

      {/* Custom Routine Creator */}
      {showCustomCreator && (
        <CustomRoutineCreator
          editingRoutine={editingRoutine}
          onSave={handleSaveCustomRoutine}
          onCancel={() => {
            setShowCustomCreator(false)
            setEditingRoutine(null)
          }}
        />
      )}

      {/* Celebrations */}
      {showCelebration && (
        <RoutineCelebration
          type={showCelebration.type}
          message={showCelebration.message}
          blockName={showCelebration.blockName}
          streakCount={showCelebration.streakCount}
          onClose={() => setShowCelebration(null)}
        />
      )}

      {/* Dopa Support */}
      {showDopaSupport && (
        <DopaRoutineSupport
          type={showDopaSupport.type}
          context={showDopaSupport.context}
          onClose={() => setShowDopaSupport(null)}
        />
      )}
    </div>
  )
}

export default RoutineBuilder